/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import TestIds from "../../../testIds";
import { addGST } from "../../../util/addGst";
import { formatPrice } from "../../../util/helpers";
import { DeliveryMode, SalesLine } from "../../orders/storage/types";
import { mapLineStatus } from "../../orders/utils/getOrderStatus";
import { Label, PriceStyle } from "../components/ProductCard/types";
import { Product } from "../storage";

export const getProductPrices = ({
  item,
  quantity = 1,
  isTfo = false,
}: {
  item: Product;
  quantity?: number;
  isTfo?: boolean;
}) => {
  const hasEditedPrice = item?.editedPriceExcGst && item.editedPriceExcGst > 0;

  const label = isTfo && hasEditedPrice ? "Edited Price: " : "Your price: ";
  let value = item.discountedPriceExcGst <= 0.0 ? "N/A" : formatPrice(item.discountedPriceExcGst);
  let valueWithGst = item.discountedPriceIncGst;
  if (item?.editedPriceExcGst && item.editedPriceExcGst > 0) {
    value = formatPrice(item.editedPriceExcGst) ?? "N/A";
    valueWithGst = addGST(item.editedPriceExcGst);
  }

  const items: Label[] = [
    {
      type: PriceStyle.Discounted,
      label,
      gstLabel: "GST excl.",
      value,
      valueWithGst,
      testId: TestIds.feat.product.card.discountedPrice,
    },
  ];
  if (item.discountedPriceExcGst < item.basePriceExcGst) {
    items.push({
      type: PriceStyle.RRP,
      label: "RRP: ",
      gstLabel: "GST excl.",
      value: item.basePriceExcGst <= 0.0 ? "N/A" : formatPrice(item.basePriceExcGst),
      valueWithGst: undefined,
      testId: TestIds.feat.product.card.nationalPrice,
    });
  }

  if (item?.originalPriceExcGst) {
    const originalPrice = quantity * item?.originalPriceExcGst;

    if (isTfo && hasEditedPrice && originalPrice > item.basePriceExcGst) {
      items.push({
        type: PriceStyle.RRP,
        label: "RRP: ",
        gstLabel: "GST excl.",
        value: formatPrice(originalPrice),
        valueWithGst: undefined,
        testId: TestIds.feat.product.card.nationalPrice,
      });
    }
  }
  return items;
};

export const getOrderPrices = ({
  item,
  deliveryMode,
  createdAt,
  isTfo = false,
}: {
  item: SalesLine;
  deliveryMode: string;
  createdAt: Date;
  isTfo?: boolean;
}) => {
  const lineStatus = mapLineStatus(item.detailedLineStatus, deliveryMode, createdAt);
  const isCardTransaction = deliveryMode === DeliveryMode.Card;

  const priceOverriddenLabel = isTfo ? "Edited Price: " : "Discounted price: ";
  const label = item.isPriceOverridden ? priceOverriddenLabel : "Your price: ";

  const items: Label[] = [
    {
      type: lineStatus?.display === "Cancelled" ? PriceStyle.Cancelled : PriceStyle.Discounted,
      label,
      gstLabel: isCardTransaction ? "GST incl." : "GST excl.",
      value: formatPrice(item.discountedPriceExcGst),
      // valueWithGst: isCardTransaction ? undefined : item.discountedPriceIncGst,
      testId: TestIds.feat.product.card.discountedPrice,
    },
  ];
  if (item.discountedPriceExcGst < item.basePriceExcGst) {
    items.push({
      type: PriceStyle.RRP,
      label: "RRP: ",
      gstLabel: isCardTransaction ? "GST incl." : "GST excl.",
      value: formatPrice(item.basePriceExcGst),
      valueWithGst: undefined,
      testId: TestIds.feat.product.card.nationalPrice,
    });
  }

  if (item?.isPriceOverridden) {
    const originalPrice = item.quantity * (item?.originalPriceExcGst ?? 0);

    if (originalPrice > item.basePriceExcGst) {
      items.push({
        type: PriceStyle.RRP,
        label: "RRP: ",
        gstLabel: isCardTransaction ? "GST incl." : "GST excl.",
        value: formatPrice(originalPrice),
        valueWithGst: undefined,
        testId: TestIds.feat.product.card.nationalPrice,
      });
    }
  }
  return items;
};
